import { useState } from 'react';
import { submitContactForm } from '../services/iframeService';
import { FormState, FormErrors, ContactFormPayload } from '../../../types';

const PHONE_REGEX = /^05\d{8}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SAFE_TEXT_REGEX = /^[^<>]*$/;

const INITIAL_FORM: FormState = {
    fullName: '',
    phone: '',
    email: '',
    topic: '',
    termsAccepted: false,
    website: '',
};

const INITIAL_ERRORS: FormErrors = {
    fullName: '',
    phone: '',
    email: '',
    topic: '',
    termsAccepted: '',
};
function validateField(name: keyof FormState, value: FormState[keyof FormState]): string {
    switch (name) {
        case 'fullName':
            if ((value as string).trim() === '') return 'נא להזין שם מלא';
            return !SAFE_TEXT_REGEX.test(value as string) ? 'תווים < או > אינם מורשים' : '';
        case 'phone':
            if ((value as string).trim() === '') return 'נא להזין מספר טלפון';
            return !PHONE_REGEX.test(value as string) ? 'ספרות בלבד, למשל: 0501234567' : '';
        case 'email':
            if ((value as string).trim() === '') return 'נא להזין כתובת אימייל';
            return !EMAIL_REGEX.test(value as string) ? 'כתובת אימייל לא תקינה' : '';
        case 'topic':
            return (value as string) === '' ? 'נא לבחור נושא פניה' : '';
        case 'termsAccepted':
            return !(value as boolean) ? 'עליך להסכים לתנאים לפני שליחת ההודעה' : '';
        default:
            return '';
    }
}

function validateAll(form: FormState): { errors: FormErrors; isValid: boolean } {
    const errors: FormErrors = {
        fullName: validateField('fullName', form.fullName),
        phone: validateField('phone', form.phone),
        email: validateField('email', form.email),
        topic: validateField('topic', form.topic),
        termsAccepted: validateField('termsAccepted', form.termsAccepted),
    };
    const isValid = Object.values(errors).every((e) => e === '');
    return { errors, isValid };
}

export const useContactForm = () => {
    const [form, setForm] = useState<FormState>(INITIAL_FORM);
    const [errors, setErrors] = useState<FormErrors>(INITIAL_ERRORS);
    const [touched, setTouched] = useState<Record<keyof FormState, boolean>>({
        fullName: false,
        phone: false,
        email: false,
        topic: false,
        termsAccepted: false,
        website: false,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const handleChange = (name: keyof FormState, value: string | boolean) => {
        setForm((prev) => ({ ...prev, [name]: value }));
        if (touched[name]) {
            setErrors((prev) => ({ ...prev, [name]: validateField(name, value as FormState[typeof name]) }));
        }
    };

    const handleBlur = (name: keyof FormState) => {
        setTouched((prev) => ({ ...prev, [name]: true }));
        setErrors((prev) => ({ ...prev, [name]: validateField(name, form[name]) }));
    };
    const handleSubmit = async () => {
        setIsSuccess(false);
        setSubmitError('');

        // Mark all fields as touched so all errors are visible
        setTouched({ fullName: true, phone: true, email: true, topic: true, termsAccepted: true, website: false });

        const { errors: validationErrors, isValid } = validateAll(form);
        setErrors(validationErrors);

        if (!isValid) return; // Block request — client-side validation failed

        setIsLoading(true);

        try {
            await submitContactForm(form as ContactFormPayload);
            setForm(INITIAL_FORM);
            setErrors(INITIAL_ERRORS);
            setTouched({ fullName: false, phone: false, email: false, topic: false, termsAccepted: false, website: false });
            setIsSuccess(true);
        } catch (error: any) {
            const serverMessage = error?.response?.data?.message;
            setSubmitError(serverMessage || 'אירעה שגיאה, אנא נסה שוב');
        } finally {
            setIsLoading(false);
        }
    };

    return {
        form,
        errors,
        touched,
        isLoading,
        isSuccess,
        submitError,
        handleChange,
        handleBlur,
        handleSubmit,
    };
};
